'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const plans = [
  {
    name: 'Starter',
    glasses: '30 glasses',
    desc: '1-month supply',
    highlight: false,
    badge: null,
  },
  {
    name: 'Burn Stack',
    glasses: '90 glasses',
    desc: '3-month supply',
    highlight: true,
    badge: 'Most Popular',
  },
  {
    name: 'Night Ritual',
    glasses: '180 glasses',
    desc: '6-month supply · Free shipping',
    highlight: false,
    badge: 'Best Value',
  },
];

export default function OrderSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(1);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="order"
      ref={sectionRef}
      className="py-32 px-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0D0F1A 0%, #130E0A 50%, #0D0F1A 100%)' }}
    >
      {/* Large background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-[160px]"
          style={{ background: 'radial-gradient(ellipse, rgba(255,107,26,0.12) 0%, transparent 70%)' }}
        />
      </div>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <p className="text-citrus font-bold uppercase tracking-[0.3em] text-xs mb-4">Start Tonight</p>
          <h2 className="font-fraunces text-5xl md:text-7xl font-bold tracking-tight leading-[0.9] mb-6">
            Your first night of
            <br />
            <span className="gradient-text-citrus italic">burning fat in your sleep.</span>
          </h2>
          <p className="text-cream-muted text-lg max-w-lg mx-auto">
            Ships same day. Arrives in 2–3 business days. 60-day money-back guarantee — no questions asked.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Plans */}
          <div className="space-y-4 reveal-left">
            <p className="text-xs text-muted uppercase tracking-widest font-bold mb-6">Choose your supply</p>
            {plans?.map((plan, i) => (
              <button
                key={plan?.name}
                onClick={() => setSelected(i)}
                className={`w-full text-left rounded-2xl p-5 border transition-all duration-300 relative ${
                  selected === i
                    ? 'border-citrus/50 bg-citrus/8' :'border-white/8 glass-card hover:border-citrus/25'
                }`}
                style={{ background: selected === i ? 'rgba(255,107,26,0.06)' : undefined }}
              >
                {plan?.badge && (
                  <span
                    className="absolute -top-2.5 right-4 text-xs font-bold px-3 py-1 rounded-full"
                    style={{
                      background: plan?.highlight ? '#FF6B1A' : 'rgba(255,179,71,0.15)',
                      color: plan?.highlight ? '#fff' : '#FFB347',
                    }}
                  >
                    {plan?.badge}
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        selected === i ? 'border-citrus' : 'border-white/20'
                      }`}
                    >
                      {selected === i && (
                        <div className="w-2.5 h-2.5 rounded-full bg-citrus" />
                      )}
                    </div>
                    <div>
                      <p className="text-cream font-bold">{plan?.name}</p>
                      <p className="text-muted text-sm">{plan?.glasses} · {plan?.desc}</p>
                    </div>
                  </div>
                </div>
              </button>
            ))}

            {/* Guarantee */}
            <div className="flex items-start gap-4 p-5 rounded-2xl glass-card mt-6">
              <div className="text-3xl shrink-0">🛡️</div>
              <div>
                <p className="text-cream font-bold text-sm mb-1">60-Day Money-Back Guarantee</p>
                <p className="text-muted text-xs leading-relaxed">
                  If you don&apos;t see a measurable difference in 60 days, we&apos;ll refund every cent. No forms. No waiting. Just email us.
                </p>
              </div>
            </div>

            {/* Order Now Button */}
            {selected !== null && (
              <Link
                href="https://3qoxrm.short.gy/8E4z28"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full mt-6 py-5 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl block text-center"
                style={{
                  background: 'linear-gradient(135deg, #FF6B1A 0%, #FF8C42 100%)',
                  color: '#fff',
                  boxShadow: '0 10px 40px rgba(255,107,26,0.3)'
                }}
              >
                Watch Video
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}